import React from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Link from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { StarsParallax } from './components/backgrounds/StarsParallax'

const GOODBYE_POST_URL = 'https://docs.regolith.rocks/blog/2026/03/10/GoodBye-Regolith'
const STORE_URL = 'https://shop.regolith.rocks'
const BLUESKY_URL = 'https://bsky.app/profile/regolith.rocks'
const BLUESKY_RAYCHASER_URL = 'https://bsky.app/profile/raychaser.regolith.rocks'

export default function App(): React.ReactElement {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'auto',
      }}
    >
      <StarsParallax />
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 3,
          py: 6,
        }}
      >
        {/* Logo */}
        <Box
          component="img"
          src="/images/favicons/android-chrome-512x512.png"
          alt="Regolith Co. logo"
          sx={{
            width: { xs: 100, sm: 128 },
            height: { xs: 100, sm: 128 },
            mb: 4,
            opacity: 0.92,
            filter: 'drop-shadow(0 0 24px rgba(251, 192, 45, 0.35))',
          }}
        />

        {/* Headline */}
        <Typography
          variant="h3"
          component="h1"
          align="center"
          sx={{
            fontWeight: 700,
            color: 'primary.main',
            letterSpacing: '0.04em',
            mb: 1,
            fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' },
            textShadow: '0 0 40px rgba(251, 192, 45, 0.4)',
          }}
        >
          Regolith Co.
        </Typography>

        <Typography
          variant="h5"
          component="h2"
          align="center"
          sx={{
            color: 'text.secondary',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            fontSize: { xs: '0.85rem', sm: '1rem' },
            mb: 5,
          }}
        >
          End of Operations
        </Typography>

        <Divider
          sx={{
            width: { xs: '80%', sm: '420px' },
            borderColor: 'primary.main',
            opacity: 0.3,
            mb: 5,
          }}
        />

        {/* Subheading */}
        <Typography
          variant="h6"
          align="center"
          sx={{
            color: 'primary.light',
            fontStyle: 'italic',
            mb: 4,
            fontSize: { xs: '1rem', sm: '1.15rem' },
          }}
        >
          Thank you for mining with us.
        </Typography>

        {/* Body */}
        <Box sx={{ maxWidth: 620, mb: 5 }}>
          <Typography variant="body1" align="center" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 2.5 }}>
            After years of helping Star Citizen miners organize, scout, and refine together — it&apos;s time to power
            down the rigs. Regolith Co. has been an incredible journey, and it was built entirely out of love for the
            community.
          </Typography>

          <Typography variant="body1" align="center" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 2.5 }}>
            Thank you to every miner who logged a session, joined a crew, or shared a scouting find. You made this worth
            building.
          </Typography>

          <Typography
            variant="body1"
            align="center"
            sx={{ color: 'text.disabled', lineHeight: 1.8, fontStyle: 'italic' }}
          >
            The lights are off, but the memories remain.
            <br />— Raychaser &amp; the Regolith team
          </Typography>
        </Box>

        {/* CTA Buttons */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          justifyContent="center"
          alignItems="center"
          sx={{ mb: 3 }}
        >
          <Button
            variant="contained"
            color="primary"
            size="large"
            component={Link}
            href={GOODBYE_POST_URL}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              fontWeight: 700,
              px: 4,
              py: 1.5,
              fontSize: '0.95rem',
              letterSpacing: '0.05em',
              boxShadow: '0 0 20px rgba(251, 192, 45, 0.3)',
              '&:hover': {
                boxShadow: '0 0 32px rgba(251, 192, 45, 0.55)',
              },
            }}
          >
            Read Our Goodbye Post
          </Button>

          <Button
            variant="outlined"
            color="primary"
            size="large"
            component={Link}
            href={STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              fontWeight: 600,
              px: 4,
              py: 1.5,
              fontSize: '0.95rem',
              letterSpacing: '0.05em',
              borderColor: 'primary.main',
              opacity: 0.85,
              '&:hover': {
                opacity: 1,
                borderColor: 'primary.light',
                backgroundColor: 'rgba(251, 192, 45, 0.08)',
              },
            }}
          >
            Merch Store
          </Button>
        </Stack>

        {/* Bluesky contact links */}
        <Typography variant="body2" align="center" sx={{ color: 'text.secondary', lineHeight: 2, mb: 8 }}>
          For follow-up, reach us on Bluesky:{' '}
          <Link href={BLUESKY_URL} target="_blank" rel="noopener noreferrer" color="primary">
            @regolith.rocks
          </Link>{' '}
          for general enquiries, or{' '}
          <Link href={BLUESKY_RAYCHASER_URL} target="_blank" rel="noopener noreferrer" color="primary">
            @raychaser.regolith.rocks
          </Link>{' '}
          to contact Raychaser directly.
        </Typography>

        {/* Footer */}
        <Typography variant="caption" align="center" sx={{ color: 'text.disabled', letterSpacing: '0.08em' }}>
          © Regolith Co. All rights reserved.
        </Typography>
      </Box>
    </Box>
  )
}
